import { Controller } from "@hotwired/stimulus"
import MoreMessages from "../lib/more_messages"

const STEP_DRAW = "draw"
const STEP_TRACE = "trace"
const STEP_WALLS = "walls"
const STEP_ROOMS = "rooms"
const STEP_READY = "ready"
const FLAT_STEPS = [STEP_DRAW, STEP_TRACE]
const VIEW_2D = "2d"
const VIEW_3D = "3d"
const PAPER_VEILED = "veiled"
const PAPER_SCANNING = "scanning"
const PAPER_CLEAR = "clear"
const TRACE_MS = 6000
const TRACE_ROWS = ["scale", "walls", "doors", "windows", "openings"]
const REDRAW_DELAY_MS = 150
const VENUE = "old_barn"
const ROOM_NONE = "none"
// No one floor chosen: each room keeps its own, which is what tells the rooms apart in the flat plan.
const FLOOR_PER_ROOM = "rooms"
const GREAT_HALL = "great_hall"
// The first time the furniture step opens, the hall is set for dinner so there is something to see; the visitor can change or clear it.
const HALL_DEFAULT = "dinner"
const ROOMS = [GREAT_HALL, "bar", "kitchen", "garden_room"]

// The guided VENUE flow on the Layout tab: the visitor's venue is drawn, its walls painted, its rooms furnished. It owns the answers; the
// layout-planner controller owns the canvas. Every answer asks for a fresh drawing (`layout-venue:plan`), which floorplans draws through the
// real engine, so the plan on the right is the real thing; the numbers on the panel come back from that drawing (`summary`), never from here.
//
// The trace is a show: the example plan is shown as a faded drawing, a scan line sweeps across it (`layout-venue:paper`, drawn by the
// planner's stage) and the checklist ticks off what the drawing has. Nothing is read from a file.
export default class extends Controller {
  static targets = [
    "step", "chips", "progressLabel", "progressBar", "backButton", "continueButton", "footer", "insideOption", "outsideOption", "floorOption", "roomOption", "roomNote",
    "tracePercent", "traceBar", "traceRow", "traceValue", "readyNote", "statRooms", "statTables", "statSeats"
  ]
  static values = { steps: Array, texts: Object, floorplansOrigin: String }

  connect() {
    this.more = new MoreMessages(this.element)
    this.reset()
  }

  disconnect() {
    clearTimeout(this.redrawTimer)
    this.cancelTrace()
  }

  // The planner calls this when the visitor picks "My venue floor plan", and again on "Start over".
  start() {
    this.reset()
    this.show(STEP_DRAW)
    this.redraw()
  }

  reset() {
    clearTimeout(this.redrawTimer)
    this.cancelTrace()
    this.answers = { interior: "white", exterior: "brick", floors: FLOOR_PER_ROOM, rooms: Object.fromEntries(ROOMS.map((room) => [room, ROOM_NONE])) }
    this.summaryData = null
    this.traceDone = false
    this.ticked = new Set()
    this.shownView = null
    this.hallDefaulted = false
  }

  // ── Answers ──

  setInside(event) { this.change({ interior: event.currentTarget.dataset.value }) }
  setOutside(event) { this.change({ exterior: event.currentTarget.dataset.value }) }
  setFloors(event) { this.change({ floors: event.currentTarget.dataset.value }) }

  setRoom(event) {
    const { room, value } = event.currentTarget.dataset
    this.hallDefaulted = true
    this.change({ rooms: { ...this.answers.rooms, [room]: value } })
  }

  change(answer) {
    this.answers = { ...this.answers, ...answer }
    this.render()
    clearTimeout(this.redrawTimer)
    this.redrawTimer = setTimeout(() => this.redraw(), REDRAW_DELAY_MS)
  }

  // ── Moving between questions ──

  next() {
    const step = this.stepsValue[Math.min(this.stepsValue.indexOf(this.step) + 1, this.stepsValue.length - 1)]
    if (step === STEP_TRACE && !this.traceDone) this.startTrace()
    if (step === STEP_ROOMS) this.defaultTheHall()
    this.show(step)
  }

  back() {
    if (this.step === STEP_DRAW) return this.dispatch("leave")

    if (this.step === STEP_TRACE) this.cancelTrace()
    this.show(this.stepsValue[this.stepsValue.indexOf(this.step) - 1])
  }

  goTo(event) {
    this.show(event.currentTarget.dataset.step)
  }

  show(step) {
    this.step = step
    this.more.closeAll()
    this.stepTargets.forEach((section) => { section.hidden = section.dataset.step !== step })
    this.dispatch("paper", { detail: { state: this.paperState } })
    this.showView(FLAT_STEPS.includes(step) ? VIEW_2D : VIEW_3D)
    this.render()
  }

  // The drawing is flat while it is traced and in 3D from the walls on, where the paint shows. The visitor can switch either way after.
  showView(view) {
    if (view === this.shownView) return

    this.shownView = view
    this.dispatch("view", { detail: { view } })
  }

  get paperState() {
    if (this.step === STEP_DRAW) return PAPER_VEILED
    return this.step === STEP_TRACE && !this.traceDone ? PAPER_SCANNING : PAPER_CLEAR
  }

  defaultTheHall() {
    if (this.hallDefaulted) return

    this.hallDefaulted = true
    this.change({ rooms: { ...this.answers.rooms, [GREAT_HALL]: HALL_DEFAULT } })
  }

  // ── The trace ──

  startTrace() {
    this.cancelTrace()
    this.ticked = new Set()
    this.traceDone = false
    this.traceTimers = TRACE_ROWS.map((row, index) => setTimeout(() => { this.ticked.add(row); this.render() }, (TRACE_MS * (index + 1)) / (TRACE_ROWS.length + 1)))
    this.traceTimers.push(setTimeout(() => this.finishTrace(), TRACE_MS))
    this.dispatch("paper", { detail: { state: PAPER_SCANNING, durationMs: TRACE_MS } })
  }

  finishTrace() {
    this.ticked = new Set(TRACE_ROWS)
    this.traceDone = true
    this.dispatch("paper", { detail: { state: PAPER_CLEAR } })
    this.render()
  }

  // Leaving the trace early puts it back to the start, so it plays again from the top.
  cancelTrace() {
    this.traceTimers?.forEach(clearTimeout)
    this.traceTimers = []
    if (!this.traceDone) this.ticked = new Set()
  }

  // ── The plan ──

  redraw() {
    this.dispatch("plan", { detail: { embedUrl: this.embedUrl() } })
  }

  embedUrl() {
    const { interior, exterior, floors, rooms } = this.answers
    const params = new URLSearchParams({ venue: VENUE, interior, exterior, floors })
    Object.entries(rooms).filter(([, option]) => option !== ROOM_NONE).forEach(([room, option]) => params.append(`rooms[${room}]`, option))
    return `${this.floorplansOriginValue}/embed/guided?${params}`
  }

  // What the frame drew: { walls, doors, windows, openings, width_m, rooms, furniture: { room: { tables, seats } } }.
  summary(data) {
    if (!data.furniture) return

    this.summaryData = data
    this.render()
  }

  // ── Drawing the panel ──

  render() {
    const index = this.stepsValue.indexOf(this.step)
    const questions = this.stepsValue.length - 1
    const onReady = this.step === STEP_READY
    this.progressLabelTarget.textContent = onReady ? "" : this.text("step_of", { step: index + 1, total: questions })
    this.progressBarTarget.style.width = `${(Math.min(index + 1, questions) / questions) * 100}%`
    // Only the step label and bar go on the ready screen: on a phone the progress row also holds "Start over".
    this.progressLabelTarget.hidden = onReady
    this.progressBarTarget.parentElement.hidden = onReady
    this.backButtonTarget.hidden = onReady
    this.continueButtonTarget.hidden = onReady
    this.renderContinue()

    this.insideOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.interior))
    this.outsideOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.exterior))
    this.floorOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.floors))
    this.roomOptionTargets.forEach((option) => this.mark(option, option.dataset.value === this.answers.rooms[option.dataset.room]))
    this.renderChips()
    this.renderTrace()
    this.renderRoomNotes()
    this.renderReady()
  }

  renderContinue() {
    const button = this.continueButtonTarget
    const traceRunning = this.step === STEP_TRACE && !this.traceDone
    button.disabled = traceRunning
    if (this.step === STEP_DRAW) button.textContent = button.dataset.draw
    else if (traceRunning) button.textContent = button.dataset.drawing
    else button.textContent = this.step === STEP_ROOMS ? button.dataset.seeVenue : button.dataset.continue
  }

  mark(option, on) {
    option.setAttribute("aria-pressed", String(on))
  }

  // The answers so far, as chips; a tap goes back to that question.
  renderChips() {
    const chips = []
    if (this.step !== STEP_DRAW) chips.push([STEP_DRAW, this.text("chip_venue")])
    if ([STEP_ROOMS, STEP_READY].includes(this.step)) chips.push([STEP_WALLS, this.wallsChip()])
    this.chipsTarget.replaceChildren(...chips.map(([step, label]) => this.chip(step, label)))
  }

  // "Navy · Stone", and the floor too once one has been chosen for every room.
  wallsChip() {
    const walls = this.text("chip_walls", { inside: this.paintName(this.answers.interior), outside: this.paintName(this.answers.exterior) })
    if (this.answers.floors === FLOOR_PER_ROOM) return walls

    return this.text("chip_floors", { walls, floor: this.text(`floor_${this.answers.floors}`) })
  }

  paintName(paint) {
    return this.text(`paint_${paint}`)
  }

  chip(step, label) {
    const chip = document.createElement("button")
    chip.type = "button"
    chip.className = "fp-guided__chip"
    chip.dataset.step = step
    chip.dataset.action = "layout-venue#goTo"
    chip.textContent = label
    return chip
  }

  // Each row ticks when the show reaches it, and says what the drawing has (counted by floorplans, not here).
  renderTrace() {
    const done = TRACE_ROWS.filter((row) => this.ticked.has(row)).length
    const percent = Math.round((done / TRACE_ROWS.length) * 100)
    this.tracePercentTarget.textContent = `${percent}%`
    this.traceBarTarget.style.width = `${percent}%`
    this.traceRowTargets.forEach((row, index) => {
      const ticked = this.ticked.has(row.dataset.row)
      row.classList.toggle("fp-venue__check--done", ticked)
      this.traceValueTargets[index].textContent = ticked ? this.traceValue(row.dataset.row) : ""
    })
  }

  traceValue(row) {
    const data = this.summaryData
    if (!data) return ""

    return row === "scale" ? this.text("scale_value", { width: data.width_m }) : data[row]
  }

  renderRoomNotes() {
    this.roomNoteTargets.forEach((note) => {
      const drawn = this.summaryData?.furniture?.[note.dataset.room]
      note.textContent = drawn?.tables ? this.text(drawn.tables === 1 ? "note_one" : "note_other", drawn) : ""
    })
  }

  renderReady() {
    const data = this.summaryData
    if (!data) return

    const furniture = Object.values(data.furniture)
    this.statRoomsTarget.textContent = data.rooms
    this.statTablesTarget.textContent = furniture.reduce((sum, room) => sum + room.tables, 0)
    this.statSeatsTarget.textContent = furniture.reduce((sum, room) => sum + room.seats, 0)
    this.readyNoteTarget.textContent = this.text("ready_note", { rooms: data.rooms })
  }

  text(key, values = {}) {
    return Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), this.textsValue[key])
  }

  // "More": the last chip of every row of options (see `MoreMessages`)
  toggleMore(event) { this.more.toggle(event.currentTarget) }
  closeMore(event) { this.more.close(event.currentTarget) }
  moreKeydown(event) { this.more.keydown(event) }
}
